import React from 'react'

import classnames from 'classnames'
import { DateTime } from 'luxon'
import { Link } from 'react-router-dom'

import { api } from '../api'
import { ChannelDetailSpec } from './ChannelDetailSpec'
import { SpecSectionTitle } from './SpecSectionTitle'

export interface ChannelDetailsPanelProps {
  channelDetails: Api_ChannelsDetails.RootObject
  onShouldRefetchChannel: () => Promise<void>
}

export const ChannelDetailsPanel: React.FC<ChannelDetailsPanelProps> = ({
  channelDetails,
  onShouldRefetchChannel,
}) => {
  const user = channelDetails.channel.creator
  const traits =
    channelDetails.traits && channelDetails.traits.length > 0
      ? channelDetails.traits[0]
      : undefined

  const [isChannelLoadingStatus, setIsChannelLoadingStatus] = React.useState(
    false,
  )

  const toggleStatus = React.useCallback(async () => {
    if (isChannelLoadingStatus) {
      return
    }
    setIsChannelLoadingStatus(true)
    await api.putChannel(channelDetails.channel.id, {
      status:
        channelDetails.channel.status === 'active' ? 'archived' : 'active',
    })
    await onShouldRefetchChannel()
    setIsChannelLoadingStatus(false)
  }, [
    channelDetails.channel.id,
    channelDetails.channel.status,
    isChannelLoadingStatus,
    onShouldRefetchChannel,
  ])

  return (
    <div className="card">
      <div className="card-content">
        <p className="buttons is-hidden-tablet">
          <Link
            className="button"
            to={`/channels/${channelDetails.channel.id}`}>
            <span className="icon">
              <i className="fas fa-arrow-alt-left" />
            </span>
            <span>Back to messages</span>
          </Link>
        </p>
        <div className="media">
          <div className="media-left">
            <figure className="image is-48x48">
              {user.profilePicture?.url && (
                <img src={user.profilePicture.url} alt="" />
              )}
            </figure>
          </div>
          <div className="media-content">
            <p className="title is-5">{`${user.firstName} ${user.lastName}`}</p>
            <p className="subtitle is-6">{user.id}</p>
          </div>
        </div>

        <div className="content">
          <div>
            <div className="control mb-6">
              <button
                className={classnames('button is-small', {
                  'is-info': channelDetails.channel.status === 'active',
                  'is-loading': isChannelLoadingStatus,
                })}
                onClick={toggleStatus}>
                <span>
                  {channelDetails.channel.status === 'archived'
                    ? 'Reopen conversation'
                    : 'Archive conversation'}
                </span>
                <span className="icon is-small">
                  <i
                    className={classnames('fas', {
                      'fa-envelope-open':
                        channelDetails.channel.status === 'archived',
                      'fa-check': channelDetails.channel.status === 'active',
                    })}
                  />
                </span>
              </button>
            </div>
            <SpecSectionTitle title="🔗 Links" />
            <a
              href={`${process.env.REACT_APP_FF_DOMAIN}/users/${user.id}`}
              target="_blank"
              rel="noreferrer">
              <span className="icon-text" style={{ fontSize: 14 }}>
                <span>Profil FF</span>
                <span className="icon">
                  <i className="fas fa-external-link" />
                </span>
              </span>
            </a>
            <br />
            <a
              href={`${process.env.REACT_APP_FF_ADMIN}/#/users/show/${user.id}`}
              target="_blank"
              rel="noreferrer">
              <span className="icon-text" style={{ fontSize: 14 }}>
                <span>Profil Admin</span>
                <span className="icon">
                  <i className="fas fa-external-link" />
                </span>
              </span>
            </a>
          </div>
          <br />
          {traits && (
            <div>
              <SpecSectionTitle title="🙋‍♂️ User infos" />
              <ChannelDetailSpec
                title="🕑 Created at"
                value={`${DateTime.fromISO(user.createdAt).toLocaleString(
                  DateTime.DATETIME_MED,
                )}`}
              />
              <ChannelDetailSpec
                title="✉️ Email"
                value={`${user.email ?? user.fbEmail ?? 'no email'}`}
              />
              <ChannelDetailSpec
                title={`${
                  traits.preferred_locale?.startsWith('fr')
                    ? '🇫🇷'
                    : traits.preferred_locale?.startsWith('en')
                    ? '🇬🇧'
                    : '🗣'
                } Language`}
                value={`${traits.preferred_locale ?? 'unknown'}`}
              />
            </div>
          )}
          <br />
          {channelDetails.channel.latestContent?.metadata && (
            <div>
              <SpecSectionTitle title="📞 Device infos" />
              <ChannelDetailSpec
                title={
                  channelDetails.channel.latestContent.metadata.platform ===
                  'android'
                    ? '🤖'
                    : channelDetails.channel.latestContent.metadata.platform ===
                      'ios'
                    ? '🍏'
                    : '📱'
                }
                value={[
                  channelDetails.channel.latestContent.metadata.device?.brand,
                  channelDetails.channel.latestContent.metadata.device?.model ??
                    channelDetails.channel.latestContent.metadata.device?.id,
                ]
                  .filter((elt) => !!elt)
                  .join(' ')}
              />
              <ChannelDetailSpec
                title="OS"
                value={[
                  channelDetails.channel.latestContent.metadata.device?.system,
                  channelDetails.channel.latestContent.metadata.device
                    ?.systemVersion,
                ]
                  .filter((elt) => !!elt)
                  .join(' ')}
              />
              <ChannelDetailSpec
                title="App"
                value={`${
                  channelDetails.channel.latestContent.metadata.appVersion || ''
                } ${
                  channelDetails.channel.latestContent.metadata.buildNumber
                    ? `(${channelDetails.channel.latestContent.metadata.buildNumber})`
                    : ''
                }`.trim()}
              />
            </div>
          )}
          {traits && (
            <div>
              <br />
              <SpecSectionTitle title="💰 Premium status" />
              {traits.is_premium ? (
                <>
                  <ChannelDetailSpec
                    title="💰 Premium"
                    value={`${traits.premium_plan ?? 'plan unknown'}`}
                  />
                  <ChannelDetailSpec
                    title="💰 Total spent"
                    value={`${traits.lifetime_value ?? 0}€`}
                  />
                </>
              ) : (
                <>
                  <ChannelDetailSpec value="❌ Not premium" />
                </>
              )}
              <br />
              <SpecSectionTitle title="🏷 Tags" />
              {Object.entries(traits.fish_tags ?? {}).map(
                ([key, value]: [string, any]) => (
                  <ChannelDetailSpec
                    key={key}
                    title={key.replace('fish_catches_count__', '')}
                    value={`${value}`}
                  />
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
